import React from 'react';
import type { NavTrailCrumb, NavTrailTarget } from '@turn-connect/shared';
import { Breadcrumbs } from './Breadcrumbs';
import { PageTabs, type PageTabsProps } from './PageTabs';
import { TitleSwitch, type TitleSwitchMenu } from './TitleSwitch';
import { goToTrailTarget, trailHref } from './placeNavigation';
import './placeHeader.css';

export interface PlaceHeaderProps {
  crumbs: NavTrailCrumb[];
  /** A crumb was clicked; defaults to going there straight away. */
  onNavigate?: (target: NavTrailTarget) => void;
  /** The account's or location's logo. */
  logo?: React.ReactNode;
  title: string;
  /** The title as a switcher (account switch on account pages, location switch on location pages). */
  switchMenu?: TitleSwitchMenu | null;
  /** A title of the host's own (a switcher that searches the server), in place of `title`'s heading. */
  titleNode?: React.ReactNode;
  /** Beside the title, e.g. the live status. */
  titleExtra?: React.ReactNode;
  /** "7 locations · 2 groups". */
  subline?: React.ReactNode;
  /** Right of the name: the period toggle, the page's actions. */
  aside?: React.ReactNode;
  tabs?: PageTabsProps | null;
  /** Touch widths: breadcrumbs collapse to "‹ Parent". */
  compact?: boolean;
  testId?: string;
  /** The title row's test id (defaults to `${testId}-title`). */
  titleRowTestId?: string;
}

/**
 * The header every account, location and settings page shares: breadcrumbs,
 * the place's logo and name (a switcher where there is somewhere else to go),
 * a quiet subline, and the Dashboard | Settings tabs.
 */
export const PlaceHeader: React.FC<PlaceHeaderProps> = ({
  crumbs,
  onNavigate = goToTrailTarget,
  logo,
  title,
  switchMenu,
  titleNode,
  titleExtra,
  subline,
  aside,
  tabs,
  compact = false,
  testId = 'place-header',
  titleRowTestId,
}) => (
  <header className="ph" data-testid={testId}>
    <Breadcrumbs crumbs={crumbs} onNavigate={onNavigate} hrefFor={trailHref} compact={compact} />
    <div className="ph-id">
      {/* The logo stays beside the name at every width; the aside wraps below. */}
      <div className="ph-main">
        {logo && <div className="ph-logo">{logo}</div>}
        <div className="ph-name">
          <div className="ph-title-row" data-testid={titleRowTestId ?? `${testId}-title`}>
            {titleNode ?? <TitleSwitch title={title} menu={switchMenu} />}
            {titleExtra}
          </div>
          {subline && (
            <div className="ph-subline" data-testid={`${testId}-subline`}>
              {subline}
            </div>
          )}
        </div>
      </div>
      {aside && <div className="ph-aside">{aside}</div>}
    </div>
    {tabs && <PageTabs {...tabs} />}
  </header>
);

export default PlaceHeader;
